import { useCallback, useMemo } from "react";
import { useGetApi, useGetFile, usePostApi, useUploadFileApi } from "src/hooks";
import { getUserId } from "src/utils";

export interface IMyDocumentsItem {
  name: string;
  type: 0 | 1 | 2;
  parent: string;
  ext: string;
  len: number;
  createddate: string;
  modifydate: string;
  id: string;
}

export interface IMyDocumentsResponse {
  list: IMyDocumentsItem[];
}

export interface IMyDocumentsTreeSummary {
  folders: number;
  files: number;
  total: number;
}

interface IMyDocumentsHookResult {
  data: IMyDocumentsResponse | undefined;
  isLoading: boolean;
  refetch: (
    body?: Record<string, any>,
    override?: boolean,
  ) => Promise<IMyDocumentsResponse | undefined>;
  treeSummary: IMyDocumentsTreeSummary;
  addFile: (parent: string, file: File) => Promise<any>;
  importFile: (parent: string, file: File) => Promise<any>;
  renameFile: (target: string, name: string) => Promise<any>;
  removeFile: (target: string) => Promise<any>;
  moveFile: (parent: string, target: string) => Promise<any>;
  getFile: (target: string) => Promise<any>;
  addFolder: (parent: string, name?: string) => Promise<any>;
  renameFolder: (target: string, name: string) => Promise<any>;
  removeFolder: (target: string) => Promise<any>;
  moveFolder: (parent: string, target: string) => Promise<any>;
  getHistory: () => Promise<any>;
}

const mapMyDocuments = (data: any): IMyDocumentsResponse => {
  const list = Array.isArray(data?.data?.content?.list)
    ? data.data.content.list
    : [];

  return { list };
};

const extractMyDocumentsList = (data: any): IMyDocumentsItem[] | null => {
  const list = Array.isArray(data?.data?.list)
    ? data.data.list
    : Array.isArray(data?.list)
      ? data.list
      : null;
  return list;
};

export const useMyDocuments = (): IMyDocumentsHookResult => {
  const { data, setData, refetch, isLoading } = useGetApi<IMyDocumentsResponse>(
    {
      request: "get-settings",
      body: { params: { sect: "_files" } },
      mapper: mapMyDocuments,
    },
  );
  const { post, isLoading: isPosting } = usePostApi();
  const { upload, isUploading } = useUploadFileApi();
  const { getFile: requestFile, isLoading: isGettingFile } = useGetFile();
  const userid = getUserId();
  const syncFromResult = useCallback(
    async (result: any) => {
      const list = extractMyDocumentsList(result);

      if (list) {
        setData({ list });
        return list;
      }

      const refreshed = await refetch();
      return refreshed?.list ?? [];
    },
    [refetch, setData],
  );

  const addFile = useCallback(
    async (parent: string, file: File) => {
      const result = await upload(
        "my-file-add",
        [file],
        { parent },
        undefined,
        true,
      );

      await syncFromResult(result);
      return result;
    },
    [syncFromResult, upload],
  );

  const importFile = useCallback(
    async (parent: string, file: File) => {
      const result = await upload(
        "my-file-import",
        [file],
        { parent },
        undefined,
        true,
      );

      await syncFromResult(result);
      return result;
    },
    [syncFromResult, upload],
  );

  const runMutation = useCallback(
    async (request: string, params?: Record<string, any>) => {
      const result = await post({ request, body: { params } });
      await syncFromResult(result);
      return result;
    },
    [post, syncFromResult],
  );

  const renameFile = useCallback(
    async (target: string, name: string) => {
      return await runMutation("my-file-rename", { target, name });
    },
    [runMutation],
  );

  const removeFile = useCallback(
    async (target: string) => {
      return await runMutation("my-file-remove", { target });
    },
    [runMutation],
  );

  const moveFile = useCallback(
    async (parent: string, target: string) => {
      return await runMutation("my-file-move", { parent, target });
    },
    [runMutation],
  );

  const getFile = useCallback(
    async (target: string) => {
      const file = data?.list?.find(item => item.id === target);

      return await Promise.resolve(
        requestFile({
          request: "my-file-get",
          body: { params: { target } },
          fileName: file?.name,
        }),
      );
    },
    [data?.list, requestFile],
  );

  const addFolder = useCallback(
    async (parent: string, name?: string) => {
      return await runMutation("my-folder-add", { parent, name });
    },
    [runMutation],
  );

  const renameFolder = useCallback(
    async (target: string, name: string) => {
      return await runMutation("my-folder-rename", { target, name });
    },
    [runMutation],
  );

  const removeFolder = useCallback(
    async (target: string) => {
      return await runMutation("my-folder-remove", { target });
    },
    [runMutation],
  );

  const moveFolder = useCallback(
    async (parent: string, target: string) => {
      return await runMutation("my-folder-move", { parent, target });
    },
    [runMutation],
  );

  const getHistory = useCallback(async () => {
    return await post({
      request: "my-files-history",
      body: { params: { userid } },
    });
  }, [post, userid]);

  const treeSummary = useMemo<IMyDocumentsTreeSummary>(() => {
    const list = data?.list ?? [];

    return {
      folders: list.filter(item => item.type !== 2).length,
      files: list.filter(item => item.type === 2).length,
      total: list.length,
    };
  }, [data]);

  return {
    data,
    isLoading: isLoading || isPosting || isUploading || isGettingFile,
    refetch,
    treeSummary,
    addFile,
    importFile,
    renameFile,
    removeFile,
    moveFile,
    getFile,
    addFolder,
    renameFolder,
    removeFolder,
    moveFolder,
    getHistory,
  };
};
